import { useState } from "react";
import type { FormEvent } from "react";
import { PROBLEM_CODES, api, errorMessage, problemCode, useApiMutation, useApiQuery } from "../api";
import type { MissionProgress, TeamMember, TeamResponse, User } from "../api";
import { Alert, Button, Field, useToast } from "../components";
import { formatTokens, parseTokenInput, toBigInt } from "../format";
import { AsyncBoundary } from "../shell";
import styles from "./MissionsPage.module.css";

export type MissionsAffectationProps = {
  idTitre: string;
  mission: MissionProgress;
  /** L'ESN gère l'équipe ; l'entreprise la lit. */
  editable: boolean;
  onFait: () => void;
  onRenoncer: () => void;
};

/** Le message d'un refus du service sur l'équipe, chiffré quand il l'est. */
function messageEquipe(erreur: unknown): string | null {
  switch (problemCode(erreur)) {
    case null:
      return erreur === null ? null : errorMessage(erreur);
    case PROBLEM_CODES.notAConsultant:
      return "La personne choisie n'est pas un consultant actif.";
    case PROBLEM_CODES.notASalesRep:
      return "Le référent doit être un compte ESN actif.";
    case PROBLEM_CODES.alreadyAssigned:
      return "Ce consultant est déjà affecté au projet.";
    case PROBLEM_CODES.assignmentInUse:
      return errorMessage(erreur);
    default:
      return errorMessage(erreur);
  }
}

/** Le tarif saisi en tokens, rendu en centièmes s'il est pair et positif. */
function lireTarif(saisie: string): string | null {
  const centiemes = parseTokenInput(saisie.trim());
  if (centiemes === null || toBigInt(centiemes) <= 0n || toBigInt(centiemes) % 2n !== 0n) {
    return null;
  }
  return centiemes;
}

const sansGroupement = (montant: string) => formatTokens(montant).replace(/[\s\u202f\u00a0]/g, "");

/**
 * L'équipe d'un projet : qui y est affecté, sur quelle expertise, à quel coût
 * journalier. L'ESN y affecte, retarife et retire ; l'entreprise lit.
 */
export function MissionsAffectation({ idTitre, mission, editable, onFait, onRenoncer }: MissionsAffectationProps) {
  const equipe = useApiQuery((o) => api.getTeam(mission.id, o), [mission.id]);

  return (
    <div className={styles.formulaire}>
      <h3 id={idTitre} className={styles.panneauTitre}>
        Équipe du projet
      </h3>
      <AsyncBoundary state={equipe} errorTitle="Équipe illisible">
        {(donnees) => (
          <Equipe
            mission={mission}
            equipe={donnees}
            editable={editable}
            onChange={equipe.reload}
            onReferent={onFait}
          />
        )}
      </AsyncBoundary>
      <div className={styles.actionsPanneau}>
        <Button type="button" variant="ghost" onClick={onRenoncer}>
          Fermer
        </Button>
      </div>
    </div>
  );
}

function Equipe({
  mission,
  equipe,
  editable,
  onChange,
  onReferent,
}: {
  mission: MissionProgress;
  equipe: TeamResponse;
  editable: boolean;
  /** L'équipe a changé : seule elle est à relire, le panneau reste ouvert. */
  onChange: () => void;
  /** Le référent porte sur le projet lui-même : la carte est à relire. */
  onReferent: () => void;
}) {
  return (
    <>
      {equipe.assignments.length === 0 ? (
        <p className={styles.mentionAction}>Aucun consultant affecté pour l'instant.</p>
      ) : (
        <ul className={styles.equipe}>
          {equipe.assignments.map((membre) => (
            <Membre key={membre.id} mission={mission} membre={membre} editable={editable} onChange={onChange} />
          ))}
        </ul>
      )}
      {editable && <Ajout mission={mission} equipe={equipe} onChange={onChange} />}
      {editable && <Referent mission={mission} onChange={onReferent} />}
    </>
  );
}

function Membre({
  mission,
  membre,
  editable,
  onChange,
}: {
  mission: MissionProgress;
  membre: TeamMember;
  editable: boolean;
  onChange: () => void;
}) {
  const { notify } = useToast();
  const [tarif, setTarif] = useState(() => sansGroupement(membre.tokens_per_day));
  const retarif = useApiMutation(api.setAssignmentRate);
  const retrait = useApiMutation(api.unassignConsultant);
  const lu = lireTarif(tarif);
  const modifie = lu !== null && lu !== membre.tokens_per_day;

  async function enregistrer() {
    if (lu === null) {
      return;
    }
    if ((await retarif.run(mission.id, membre.id, { tokens_per_day: lu })) !== null) {
      notify({ tone: "success", title: `Tarif de ${membre.consultant_email} enregistré` });
      onChange();
    }
  }

  async function retirer() {
    // `run` rend null en échec (undefined en succès, la réponse est vide) :
    // c'est cette valeur qu'on teste, `retrait.error` n'est à jour qu'au rendu
    // suivant.
    if ((await retrait.run(mission.id, membre.id)) === null) {
      return;
    }
    notify({ tone: "success", title: `${membre.consultant_email} retiré du projet` });
    onChange();
  }

  const erreur = messageEquipe(retarif.error ?? retrait.error);

  return (
    <li className={styles.membre}>
      <div className={styles.membreTextes}>
        <strong>{membre.consultant_email}</strong>
        <span className={styles.mentionAction}>{membre.expertise_label}</span>
      </div>
      {editable ? (
        <div className={styles.membreActions}>
          <Field label="Tokens / jour" error={tarif !== "" && lu === null ? "Pair et positif." : undefined}>
            {(commande) => (
              <input {...commande} inputMode="decimal" value={tarif} onChange={(e) => setTarif(e.target.value)} disabled={retarif.pending} />
            )}
          </Field>
          {modifie && (
            <Button size="sm" variant="secondary" onClick={() => void enregistrer()} loading={retarif.pending}>
              Enregistrer
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => void retirer()} loading={retrait.pending}>
            Retirer
          </Button>
        </div>
      ) : (
        <span className="lde-tabular">{formatTokens(membre.tokens_per_day)} tokens/j</span>
      )}
      {erreur !== null && (
        <Alert as="p" role="alert">
          {erreur}
        </Alert>
      )}
    </li>
  );
}

function Ajout({ mission, equipe, onChange }: { mission: MissionProgress; equipe: TeamResponse; onChange: () => void }) {
  const { notify } = useToast();
  const consultants = useApiQuery((o) => api.listConsultants(o), []);
  const affectation = useApiMutation(api.assignConsultant);
  const [consultant, setConsultant] = useState("");
  const [expertise, setExpertise] = useState(equipe.expertises[0]?.id ?? "");
  const [tarif, setTarif] = useState("");

  const dejaLa = new Set(equipe.assignments.map((a) => a.consultant_id));
  const disponibles = (consultants.data?.users ?? []).filter((u: User) => u.status !== "disabled" && !dejaLa.has(u.id));
  const choisie = equipe.expertises.find((e) => e.id === expertise);
  const tarifLu = tarif.trim() === "" ? null : lireTarif(tarif);

  async function affecter(e: FormEvent) {
    e.preventDefault();
    if (consultant === "" || expertise === "" || (tarif.trim() !== "" && tarifLu === null)) {
      return;
    }
    const cree = await affectation.run(mission.id, {
      consultant_id: consultant,
      expertise_id: expertise,
      ...(tarifLu !== null ? { tokens_per_day: tarifLu } : {}),
    });
    if (cree !== null) {
      notify({ tone: "success", title: "Consultant affecté" });
      setConsultant("");
      setTarif("");
      onChange();
    }
  }

  return (
    <form className={styles.formulaire} onSubmit={(e) => void affecter(e)}>
      <fieldset className={styles.bloc} disabled={affectation.pending}>
        <Field label="Consultant" hint="Il remplira ses feuilles de temps sur ce projet.">
          {(commande) => (
            <select {...commande} value={consultant} onChange={(e) => setConsultant(e.target.value)}>
              <option value="">Choisir…</option>
              {disponibles.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}
                  {u.status === "invited" ? " (invité)" : ""}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Expertise">
          {(commande) => (
            <select {...commande} value={expertise} onChange={(e) => setExpertise(e.target.value)}>
              {equipe.expertises.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.label} · {formatTokens(x.tokens_per_day)} tokens/j
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field
          label="Tarif ajusté (tokens / jour)"
          hint={choisie !== undefined ? `Vide : ${formatTokens(choisie.tokens_per_day)}, le tarif de l'expertise.` : undefined}
          error={tarif.trim() !== "" && tarifLu === null ? "Un nombre pair de centièmes, strictement positif." : undefined}
        >
          {(commande) => <input {...commande} inputMode="decimal" value={tarif} onChange={(e) => setTarif(e.target.value)} />}
        </Field>
      </fieldset>
      {messageEquipe(affectation.error) !== null && (
        <Alert as="p" role="alert">
          {messageEquipe(affectation.error)}
        </Alert>
      )}
      <div className={styles.actionsPanneau}>
        <Button type="submit" variant="primary" loading={affectation.pending} disabled={consultant === ""}>
          Affecter au projet
        </Button>
      </div>
    </form>
  );
}

function Referent({ mission, onChange }: { mission: MissionProgress; onChange: () => void }) {
  const { notify } = useToast();
  const equipeEsn = useApiQuery((o) => api.listEsnUsers(o).catch(() => ({ users: [] })), []);
  const designation = useApiMutation(api.setMissionSalesRep);
  const [referent, setReferent] = useState(mission.sales_rep_id ?? "");

  async function enregistrer(e: FormEvent) {
    e.preventDefault();
    if ((await designation.run(mission.id, { sales_rep_id: referent === "" ? null : referent })) !== null) {
      notify({ tone: "success", title: "Référent enregistré" });
      onChange();
    }
  }

  return (
    <form className={styles.formulaire} onSubmit={(e) => void enregistrer(e)}>
      <Field label="Commercial référent" hint="Prévenu à chaque soumission de feuille. Sinon, les administrateurs ESN.">
        {(commande) => (
          <select {...commande} value={referent} onChange={(e) => setReferent(e.target.value)} disabled={designation.pending}>
            <option value="">Personne</option>
            {(equipeEsn.data?.users ?? [])
              .filter((u) => u.status === "active")
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}
                </option>
              ))}
          </select>
        )}
      </Field>
      {messageEquipe(designation.error) !== null && (
        <Alert as="p" role="alert">
          {messageEquipe(designation.error)}
        </Alert>
      )}
      <div className={styles.actionsPanneau}>
        <Button type="submit" variant="secondary" loading={designation.pending}>
          Enregistrer le référent
        </Button>
      </div>
    </form>
  );
}
